// Monthly Client Work Planner — plan, assign, schedule and track every client
// deliverable for the month. Targets are configuration; each deliverable is a real CRM
// task tagged with a service, so the Tasks pages and reporting stay in sync.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import WorkHubBreadcrumb from "@/pages/work-hub/WorkHubBreadcrumb";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, Plus, Search, RefreshCw, Loader2, LayoutGrid, CalendarDays,
  KanbanSquare, Users, Target, FileBarChart, Sparkles, Filter, X, Wand2, AlertTriangle, CalendarClock, UserCheck, Building2,
} from "lucide-react";
import { useBusiness } from "@/contexts/BusinessContext";
import { mpApi, thisMonth, shiftMonth, SERVICE_KEYS, serviceMeta, chipProps, setServices, STATUS_KEYS, statusMeta, PRIORITIES } from "./mpShared";
import OverviewTab from "./OverviewTab";
import CalendarTab from "./CalendarTab";
import PlanningTab from "./PlanningTab";
import WorkloadTab from "./WorkloadTab";
import TargetsTab from "./TargetsTab";
import ReportsTab from "./ReportsTab";
import TaskDialog from "./TaskDialog";
import GenerateDialog from "./GenerateDialog";
import ClientDialog from "./ClientDialog";
import ServicesDialog from "./ServicesDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TABS = [
  ["overview", "Overview", LayoutGrid],
  ["calendar", "Calendar", CalendarDays],
  ["planning", "Planning Board", KanbanSquare],
  ["workload", "Team Workload", Users],
  ["targets", "Service Targets", Target],
  ["reports", "Reports", FileBarChart],
];
const EMPTY_FILTERS = { client_id: "", service: "", status: "", bucket: "", assigned_to: "", priority: "", search: "", overdue: false };

// Work Hub sections: each page shows its own view(s) of the same planner data.
const SECTIONS = {
  overview: { tabs: ["overview"], crumb: "Overview", title: "Work Hub", subtitle: "Client work at a glance — monthly progress, deadlines and workload" },
  planner: { tabs: ["planning", "targets"], crumb: "Work Planner", title: "Work Planner", subtitle: "Plan the month: spread work across weeks and set each client's service targets" },
  calendar: { tabs: ["calendar"], crumb: "Calendar", title: "Calendar", subtitle: "All tasks and planned client work by month, week or day" },
  workload: { tabs: ["workload"], crumb: "Team Workload", title: "Team Workload", subtitle: "Assigned, completed, pending and overdue work for each team member" },
  reports: { tabs: ["reports"], crumb: "Reports", title: "Reports", subtitle: "Client, service and employee reports — export to PDF, Excel or CSV" },
};
const TAB_PATH = { overview: "/work-hub", planning: "/work-hub/planner", targets: "/work-hub/planner", calendar: "/work-hub/calendar", workload: "/work-hub/team-workload", reports: "/work-hub/reports" };

export default function MonthlyPlanner({ section }) {
  const location = useLocation();
  const navigate = useNavigate();
  const sec = SECTIONS[section] || { tabs: TABS.map(([k]) => k), crumb: "Work Planner", title: "Monthly Client Work Planner", subtitle: "Plan, assign and track monthly creative work for all clients" };
  const carried = location.state || {};
  const { businessId } = useBusiness();
  const [month, setMonth] = useState(() => carried.month || thisMonth());
  const [tab, setTab] = useState(() => (sec.tabs.includes(carried.tab) ? carried.tab : sec.tabs[0]));
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [me, setMe] = useState(null);
  const [filters, setFilters] = useState(() => ({ ...EMPTY_FILTERS, ...(carried.filters || {}) }));
  const [showFilters, setShowFilters] = useState(false);
  const [search, setSearch] = useState("");
  const [taskDialog, setTaskDialog] = useState(null); // { task } | { defaults }
  const [showGenerate, setShowGenerate] = useState(false);
  const [showNewClient, setShowNewClient] = useState(false);
  const [showServices, setShowServices] = useState(false);
  const [newClientId, setNewClientId] = useState(() => carried.highlight || "");
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/auth/me`, { credentials: "include" }).then((r) => (r.ok ? r.json() : null)).then(setMe).catch(() => {});
    fetch(`${BACKEND_URL}/api/team/members`, { credentials: "include" }).then((r) => r.json())
      .then((d) => setMembers(Array.isArray(d) ? d.filter((m) => m.status === "approved").map((m) => ({ user_id: m.user_id, name: (m.name || m.email || "").trim() })) : []))
      .catch(() => setMembers([]));
  }, []);

  useEffect(() => { const t = setTimeout(() => setFilters((f) => ({ ...f, search })), 350); return () => clearTimeout(t); }, [search]);

  const load = useCallback(() => {
    setLoading(true);
    return mpApi("/overview", { params: { month, business_id: businessId || undefined } })
      .then((d) => { setServices(d.services); setOverview(d); })
      .catch((e) => toast.error(e.message))
      .finally(() => setLoading(false));
  }, [month, businessId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);
  const canManage = !!me && ["super_admin", "admin", "manager"].includes(me.role);
  const clientOptions = useMemo(() => (overview ? overview.clients.map((c) => ({ client_id: c.client_id, name: c.name })) : []), [overview]);
  const activeFilterCount = Object.entries(filters).filter(([k, v]) => v && k !== "search").length + (filters.search ? 1 : 0);

  const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  // Switch view: stay on this page when it owns the tab, otherwise go to that Work Hub page
  // carrying the month and filters along.
  const goTab = (k, nextFilters = filters, extra = {}) => {
    if (sec.tabs.includes(k)) { setTab(k); setFilters(nextFilters); return; }
    navigate(TAB_PATH[k] || "/work-hub", { state: { month, filters: nextFilters, tab: k, ...extra } });
  };
  const quick = {
    overdue: () => goTab("calendar", { ...EMPTY_FILTERS, overdue: true }),
    week: () => goTab("planning"),
    mine: () => { if (me) goTab("calendar", { ...EMPTY_FILTERS, assigned_to: me.user_id }); },
  };

  const shared = { month, businessId, filters, setFilter, members, clients: clientOptions, canManage, me, refresh,
    openTask: (task) => setTaskDialog({ task }), newTask: (defaults) => setTaskDialog({ defaults }) };

  return (
    <div className="space-y-4 p-4 md:p-6" data-testid="monthly-planner">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <WorkHubBreadcrumb current={sec.crumb} className="mb-2" />
          <h1 className="text-2xl font-semibold text-slate-900">{sec.title}</h1>
          <p className="text-sm text-slate-500">{sec.subtitle}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center rounded-lg border border-slate-200 bg-white">
            <button onClick={() => setMonth((m) => shiftMonth(m, -1))} title="Previous month" className="rounded-l-lg px-2 py-2 text-slate-500 hover:bg-slate-50"><ChevronLeft className="h-4 w-4" /></button>
            <input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} data-testid="mp-month"
              className="w-[172px] border-x border-slate-200 px-2 py-2 text-sm font-medium text-slate-800 outline-none" />
            <button onClick={() => setMonth((m) => shiftMonth(m, 1))} title="Next month" className="rounded-r-lg px-2 py-2 text-slate-500 hover:bg-slate-50"><ChevronRight className="h-4 w-4" /></button>
          </div>
          {month !== thisMonth() && <button onClick={() => setMonth(thisMonth())} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50">This month</button>}
          <button onClick={load} title="Refresh" className="rounded-lg border border-slate-200 bg-white p-2 text-slate-500 hover:bg-slate-50"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /></button>
          <button onClick={() => setShowNewClient(true)} data-testid="mp-new-client"
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            <Building2 className="h-4 w-4" /> New Client
          </button>
          {canManage && (
            <button onClick={() => setShowGenerate(true)} data-testid="mp-generate"
              className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700">
              <Sparkles className="h-4 w-4" /> Generate Monthly Tasks
            </button>
          )}
          <button onClick={() => setTaskDialog({ defaults: { client_id: filters.client_id || "" } })} data-testid="mp-add-task"
            className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700">
            <Plus className="h-4 w-4" /> Add Task
          </button>
        </div>
      </div>

      {/* Tabs + search + filters */}
      <div className="flex flex-wrap items-center gap-2">
        {sec.tabs.length > 1 && <div className="flex w-full gap-1 overflow-x-auto rounded-xl border border-slate-200 bg-white p-1 xl:w-auto xl:flex-none">
          {TABS.filter(([k]) => sec.tabs.includes(k)).map(([k, label, Icon]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`mp-tab-${k}`}
              className={`inline-flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition ${tab === k ? "bg-slate-900 text-white shadow-sm" : "text-slate-600 hover:bg-slate-100"}`}>
              <Icon className="h-4 w-4" />{label}
            </button>
          ))}
        </div>}
        <div className="relative min-w-[200px] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search client, task or employee…"
            className="h-10 w-full rounded-lg border border-slate-200 pl-8 pr-3 text-sm text-slate-800 focus:border-indigo-400 focus:outline-none" />
        </div>
        <button onClick={() => setShowFilters((v) => !v)} data-testid="mp-filters"
          className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium ${activeFilterCount ? "border-indigo-300 bg-indigo-50 text-indigo-700" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
          <Filter className="h-4 w-4" /> Filters{activeFilterCount ? ` (${activeFilterCount})` : ""}
        </button>
      </div>

      {showFilters && (
        <div className="flex flex-wrap items-end gap-2 rounded-xl border border-slate-200 bg-white p-3">
          <Select label="Client" value={filters.client_id} onChange={(v) => setFilter("client_id", v)} options={[["", "All clients"], ...clientOptions.map((c) => [c.client_id, c.name])]} />
          <Select label="Service" value={filters.service} onChange={(v) => setFilter("service", v)} options={[["", "All services"], ...SERVICE_KEYS.map((k) => [k, serviceMeta(k).label]), ["none", "No service tag"]]} />
          <Select label="Status" value={filters.status} onChange={(v) => setFilter("status", v)} options={[["", "All statuses"], ...STATUS_KEYS.map((k) => [k, statusMeta(k).label])]} />
          <Select label="Employee" value={filters.assigned_to} onChange={(v) => setFilter("assigned_to", v)} options={[["", "Anyone"], ...members.map((m) => [m.user_id, m.name])]} />
          <Select label="Priority" value={filters.priority} onChange={(v) => setFilter("priority", v)} options={[["", "Any priority"], ...PRIORITIES.map(([v, l]) => [v, l])]} />
          <label className="inline-flex h-10 items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 text-sm text-slate-600">
            <input type="checkbox" checked={filters.overdue} onChange={(e) => setFilter("overdue", e.target.checked)} /> Overdue only
          </label>
          <div className="ml-auto flex gap-1.5">
            <button onClick={quick.overdue} className="inline-flex items-center gap-1 rounded-lg bg-rose-50 px-2.5 py-2 text-xs font-medium text-rose-700 hover:bg-rose-100"><AlertTriangle className="h-3.5 w-3.5" /> Show overdue</button>
            <button onClick={quick.week} className="inline-flex items-center gap-1 rounded-lg bg-slate-100 px-2.5 py-2 text-xs font-medium text-slate-700 hover:bg-slate-200"><CalendarClock className="h-3.5 w-3.5" /> This week</button>
            <button onClick={quick.mine} className="inline-flex items-center gap-1 rounded-lg bg-slate-100 px-2.5 py-2 text-xs font-medium text-slate-700 hover:bg-slate-200"><UserCheck className="h-3.5 w-3.5" /> My tasks</button>
            {activeFilterCount > 0 && <button onClick={() => { setFilters(EMPTY_FILTERS); setSearch(""); }} className="inline-flex items-center gap-1 rounded-lg px-2.5 py-2 text-xs font-medium text-slate-500 hover:bg-slate-100"><X className="h-3.5 w-3.5" /> Clear</button>}
          </div>
        </div>
      )}

      {overview && overview.totals.untagged > 0 && tab === "overview" && canManage && (
        <UntaggedNotice month={month} businessId={businessId} count={overview.totals.untagged} onDone={refresh} />
      )}

      {loading && !overview ? (
        <div className="flex items-center justify-center py-24 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : (
        <>
          {tab === "overview" && <OverviewTab {...shared} overview={overview} loading={loading} onManageServices={() => setShowServices(true)} />}
          {tab === "calendar" && <CalendarTab {...shared} />}
          {tab === "planning" && <PlanningTab {...shared} overview={overview} />}
          {tab === "workload" && <WorkloadTab {...shared} />}
          {tab === "targets" && <TargetsTab {...shared} onSaved={refresh} onAddClient={() => setShowNewClient(true)} onManageServices={canManage ? () => setShowServices(true) : undefined} highlightClientId={newClientId} refreshKey={refreshKey} />}
          {tab === "reports" && <ReportsTab {...shared} overview={overview} />}
        </>
      )}

      <p className="pt-2 text-xs text-slate-400">
        Every deliverable is a real CRM task, so the planner, <Link to="/tasks" className="font-medium text-indigo-600 hover:underline">Tasks</Link> and
        {" "}<Link to="/work-planner/performance" className="font-medium text-indigo-600 hover:underline">Client performance</Link> always show the same numbers.
      </p>

      <TaskDialog
        open={!!taskDialog} onClose={() => setTaskDialog(null)}
        task={taskDialog?.task} defaults={taskDialog?.defaults}
        clients={clientOptions.length ? clientOptions : []} members={members}
        onSaved={refresh}
      />
      <ServicesDialog open={showServices} onClose={() => setShowServices(false)} businessId={businessId} onChanged={refresh} />
      <ClientDialog open={showNewClient} onClose={() => setShowNewClient(false)} businessId={businessId} canManage={canManage}
        onCreated={(client) => {
          setNewClientId(client.client_id);
          refresh();
          // A client with no targets and no work yet only shows under Service Targets.
          if (canManage) goTab("targets", filters, { highlight: client.client_id });
        }} />
      <GenerateDialog open={showGenerate} onClose={() => setShowGenerate(false)} month={month} businessId={businessId}
        clients={overview ? overview.clients : []} members={members} onDone={refresh} />
    </div>
  );
}

function Select({ label, value, onChange, options }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] font-medium uppercase tracking-wide text-slate-400">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="h-10 min-w-[140px] rounded-lg border border-slate-200 px-2 text-sm text-slate-700">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
}

/** Existing tasks predate service tags — offer a preview-then-apply auto-tagger. */
function UntaggedNotice({ month, businessId, count, onDone }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;

  const run = async (dry) => {
    setBusy(true);
    try {
      const r = await mpApi("/classify", { method: "POST", body: { month, business_id: businessId || undefined, dry_run: dry } });
      if (dry) setPreview(r);
      else { toast.success(`${r.matched} task${r.matched === 1 ? "" : "s"} tagged`); setPreview(null); setDismissed(true); onDone(); }
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
      <div className="flex flex-wrap items-center gap-2 text-sm text-amber-900">
        <Wand2 className="h-4 w-4 shrink-0" />
        <span><b>{count}</b> task{count === 1 ? "" : "s"} this month have no service type, so they don't count towards targets.</span>
        <div className="ml-auto flex gap-1.5">
          <button onClick={() => run(true)} disabled={busy} className="rounded-lg bg-white px-2.5 py-1.5 text-xs font-semibold text-amber-800 ring-1 ring-amber-300 hover:bg-amber-100">
            {busy && !preview ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Preview auto-tagging"}
          </button>
          <button onClick={() => setDismissed(true)} className="rounded-lg px-2 py-1.5 text-xs text-amber-700 hover:bg-amber-100">Dismiss</button>
        </div>
      </div>
      {preview && (
        <div className="mt-2 rounded-lg bg-white p-3 text-xs text-slate-700">
          <div className="font-semibold text-slate-900">{preview.matched} of {preview.untagged} can be tagged automatically</div>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {Object.entries(preview.by_service).map(([k, n]) => <span key={k} {...chipProps(k, "rounded px-1.5 py-0.5 font-medium")}>{serviceMeta(k).label}: {n}</span>)}
          </div>
          {preview.samples?.length > 0 && <div className="mt-2 text-slate-500">e.g. {preview.samples.slice(0, 4).map((s) => `“${s.title}” → ${serviceMeta(s.service).label}`).join(" · ")}</div>}
          {preview.unmatched > 0 && <div className="mt-1 text-slate-500">{preview.unmatched} task(s) stay untagged (e.g. {(preview.unmatched_samples || []).slice(0, 3).map((s) => `“${s.title}”`).join(", ")}). You can tag those by hand.</div>}
          <div className="mt-2 flex gap-2">
            <button onClick={() => run(false)} disabled={busy} className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : `Apply to ${preview.matched} task(s)`}
            </button>
            <button onClick={() => setPreview(null)} className="rounded-lg px-3 py-1.5 text-xs font-medium text-slate-500 hover:bg-slate-100">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

